import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  NgZone,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputNumber } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import { NegativeHighlightDirective } from '../../../../directives/negative-highlight.directive';
import { NegativeLabelPipe } from '../../../../pipes/negative-label.pipe';
import { DEBUG_FORM_TICK_DEBOUNCE_MS } from '../../../../utils/debounce-times.util';

/** Column sum definition mirrored from the host schema (structural only). */
export interface DebugColumnSumConfig {
  subTotalColumnQname: string;
  componentColumnQnames: string[];
  subTotalLabel: string;
  componentLabels: string[];
}

/**
 * Narrow read surface the debug layout needs from its host component.
 * Kept here so the host stays free to evolve its private API without
 * dragging the debug layout along.
 */
export interface Role19DebugHost {
  showConsolidatedColumns: boolean;
  hasPreviousYear: boolean;
  periodHeaderCurrent: string;
  periodHeaderPrevious: string;

  isSyncValidationComplete(): boolean;
  isSectionHeader(control: DebugFieldControl): boolean;
  isCalculatedField(control: DebugFieldControl): boolean;
  isRowInEditMode(control: DebugFieldControl, consolidationType?: string, periodType?: string): boolean;
  enterEditMode(control: DebugFieldControl, consolidationType?: string, periodType?: string): void;
  saveEditMode(control: DebugFieldControl, consolidationType?: string, periodType?: string): void;
  cancelEditMode(control: DebugFieldControl, consolidationType?: string, periodType?: string): void;
  shouldShowRow(
    control: DebugFieldControl,
    index: number,
    consolidationType: string,
    periodType: string,
  ): boolean;
  getEquityColumnControlName(
    rowQname: string,
    columnQname: string,
    consolidationType: string,
    periodType: string,
  ): string;
  getRowValidationStatus(
    control: DebugFieldControl,
    consolidationType: string,
    periodType: string,
  ): 'Failed' | 'Edited' | 'Validated*' | 'Validated';
  getRowErrorDescription(
    control: DebugFieldControl,
    consolidationType: string,
    periodType: string,
  ): string;
  hasRowValidationError(
    control: DebugFieldControl,
    consolidationType: string,
    periodType: string,
  ): boolean;
  hasCrossSheetError(
    rowQname: string,
    columnQname: string,
    consolidationType: string,
    periodType: string,
  ): boolean;
  hasRequiredError(
    control: DebugFieldControl,
    columnQname: string,
    consolidationType: string,
    periodType: string,
  ): boolean;
  hasColumnSumError(
    control: DebugFieldControl,
    columnQname: string,
    consolidationType: string,
    periodType: string,
  ): boolean;
  hasEquityTotalError(
    control: DebugFieldControl,
    columnQname: string,
    consolidationType: string,
    periodType: string,
  ): boolean;
  hasEquityBeginningEndError(
    control: DebugFieldControl,
    columnQname: string,
    consolidationType: string,
    periodType: string,
  ): boolean;

  // New public surface used by enhanced debug features.
  getColumnSumValidations(): DebugColumnSumConfig[];
  getFormStats(): { dirty: number; touched: number; disabled: number; total: number };
  setFieldValue(controlName: string, value: number | null): boolean;

  /**
   * Row-level validation messages keyed by row qname — the same bag that
   * feeds `getRowErrorDescription`. Used by the debug layout's `cellErrors`
   * to map row-level aggregation errors (e.g. "Equity attributable to
   * owners should equal to the sum of …") back onto the subtotal cell
   * they belong to as a short chip. Unmapped messages are dropped on
   * purpose — the existing `<details>` block already surfaces them below
   * the row.
   */
  getRowValidationErrors(rowQname: string): string[];
}

export interface DebugEquityColumn {
  qname: string;
  label: string;
  order?: number;
  calculated?: boolean;
}

export interface DebugFieldControl {
  qname: string;
  label: string;
  type?: string;
  ui: {
    controlType?: string;
    equityColumns?: boolean;
    level?: number;
    readonly?: boolean;
    [key: string]: unknown;
  };
}

export interface DebugFormSchema {
  equityColumns?: DebugEquityColumn[];
  controls: DebugFieldControl[];
}

type Period = 'Current' | 'Previous';
type RowSnapshot = Record<string, number | null>;

/**
 * Role 19 — Statement of Changes in Equity: Refactored Developer Layout.
 *
 * This is a **non-production, opt-in** alternative view of the Role 19
 * sheet. The original 4-table grid (Consolidated/Separate × Current/Previous,
 * up to 26 equity columns × 15+ rows) is painful to scan when hunting
 * validation bugs; this component rebuilds that grid as a per-row card with
 * paired Current/Previous cells + a Δ column + inline validation chips, plus
 * a banner of filter toggles, row actions, and a two-pane split mode.
 *
 * Live reference: `docs/ui/conversion-sheets/role19-socie/role19-debug-layout.md`
 *
 * ─── 1. How the setup is wired ───────────────────────────────────────
 *
 * Three files conspire:
 *
 *   a) `services/common/debug-layout-toggle.service.ts`
 *      Singleton (`providedIn: 'root'`). Holds the `enabled` signal and
 *      listens for `Ctrl+Alt+D` via a ref-counted window keydown handler.
 *      Its `bindGlobalShortcut(destroyRef)` is a **no-op** when
 *      `environment.production === true` — the `if` uses the raw const so
 *      the body is dead-code-eliminated at build time.
 *
 *   b) The host sheet `role19-610000-statement-of-changes-in-equity.component.ts`
 *      - Implements `Role19DebugHost` (this file's interface above).
 *      - Injects the toggle service and calls `bindGlobalShortcut` in
 *        `ngOnInit`.
 *      - Exposes `getColumnSumValidations`, `getFormStats`, `setFieldValue`
 *        on top of the existing validation / edit-mode surface so the
 *        debug layout can read + write without reaching into privates.
 *
 *   c) This component
 *      - Gets `form`, `schema`, `host` as required inputs from the host's
 *        template: `<app-role19-debug-layout [form]="..." [schema]="..."
 *        [host]="this">`.
 *      - Is rendered only when the host template's outer guard passes —
 *        the guard is `!envService.isProduction() && debugToggle.enabled()`.
 *
 * When the user hits `Ctrl+Alt+D` the signal flips, Angular swaps out the
 * original 4-table grid, and this component takes its place **inside the
 * same `<p-card>`**; Submit / Cancel stay at the bottom of the page.
 *
 * Adding the debug layout to another sheet = implement the same narrow
 * host interface on that sheet, then add the guarded `<app-role19-debug-
 * layout>` tag (or an equivalent sibling component for that sheet) to its
 * template.
 *
 * ─── 2. Environments where this is allowed to render ─────────────────
 *
 * Allowed: `development`, `staging`, `uat` (anything where
 * `environment.production === false`).
 *
 * Forbidden: `production` (`environment.production === true`).
 *
 * Two overlapping gates enforce that:
 *
 *   - **Service-level:** `DebugLayoutToggleService.bindGlobalShortcut`
 *     short-circuits on `environment.production`, so the keydown listener
 *     is never attached and the toggle signal stays `false` forever.
 *   - **Template-level:** the host wraps `<app-role19-debug-layout>` in
 *     `*ngIf="!envService.isProduction() && debugToggle.enabled()"`, so
 *     even if the signal were forced true somehow, the component would
 *     never render.
 *
 * **If this ever becomes visible in production, do NOT assume the gate is
 * broken — first verify the production bundle was actually built with the
 * `production` configuration.** Check in order:
 *
 *   1. `angular.json` → the active configuration for the deployed build
 *      has `fileReplacements` pointing at `environment.production.ts`.
 *   2. `src/environments/environment.production.ts` → confirm the file
 *      exports `production: true` and `env: 'production'`.
 *   3. CI / deploy command → the build step used
 *      `ng build --configuration production` (or `npm run build:production`
 *      / `b:p`), not the default dev build.
 *   4. In the running bundle, paste into the console:
 *      `fetch('/assets/.../environment-marker.json').then(r => r.json())`
 *      (if used) — or inspect `window.ng?.getInjector(...)` — to confirm
 *      which env file was baked in.
 *
 * Declaring a prod deployment under a non-prod configuration is the only
 * plausible reason this component would appear there; fix the build
 * pipeline before changing any code here.
 *
 * ─── 3. Signs this page is the performance culprit, and how to shed it ─
 *
 * The refactored layout instantiates one `p-inputNumber` per cell × per
 * consolidation × per period. On Role 19 that is ~2,400 PrimeNG component
 * instances at once. Symptoms to watch for **when the debug layout is
 * active**:
 *
 *   - Initial toggle takes multiple seconds before inputs are interactive,
 *     with rows visibly painting one-by-one.
 *   - Typing in a cell has noticeable lag even after load.
 *   - Browser devtools show long-running `ControlValueAccessor` /
 *     `writeValue` / `setValue` scripting frames on the main thread.
 *   - Memory grows every time the user toggles the debug mode on/off.
 *
 * **If you see this and confirm it is driven by the debug layout (i.e. the
 * problem disappears when the toggle is off), you have three escape
 * hatches, in increasing order of effort / commitment:**
 *
 *   a) **Tell the user the faster route.** The toggle is session-only:
 *      a page refresh restores the original 4-table grid. No code change.
 *
 *   b) **Disable the toggle service globally** so `Ctrl+Alt+D` becomes a
 *      no-op everywhere (useful when a specific non-prod env needs to
 *      stop shipping the debug bundle's runtime effect but you cannot
 *      redeploy a new build):
 *
 *         // src/app/services/common/debug-layout-toggle.service.ts
 *         bindGlobalShortcut(destroyRef: DestroyRef): void {
 *           return; // temporarily disabled — see ticket XYZ
 *         }
 *
 *   c) **Delete the debug tag from the host template** so the component is
 *      tree-shaken out entirely (keeps the host + service intact for later
 *      re-enable). Remove:
 *
 *         <app-role19-debug-layout
 *           *ngIf="!envService.isProduction() && debugToggle.enabled()"
 *           ...>
 *         </app-role19-debug-layout>
 *
 *   d) **Full removal** is described in the "Removal" section of the
 *      markdown reference above — five files, one commit, zero scar on
 *      the original layout.
 *
 * Before reaching for (a)–(d), please verify the issue is actually this
 * component and not the host's validation pipeline — the host's
 * `rebuildValidationCaches`, `EqualityValidationService`, and
 * `CrossReferenceService` all run regardless of which layout is active.
 */
@Component({
  selector: 'app-role19-debug-layout',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputNumber,
    MessageModule,
    NegativeHighlightDirective,
    NegativeLabelPipe,
  ],
  templateUrl: './role19-debug-layout.component.html',
  styleUrls: ['./role19-debug-layout.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Role19DebugLayoutComponent {
  @Input({ required: true }) form!: FormGroup;
  @Input({ required: true }) schema!: DebugFormSchema;
  @Input({ required: true }) host!: Role19DebugHost;

  readonly maxValue = 999_999_999_999.99;

  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostEl: ElementRef<HTMLElement> = inject(ElementRef);

  // ─── View state ───────────────────────────────────────────────────
  readonly showScrollTop = signal(false);
  readonly onlyFailing = signal(false);
  readonly hideCalculated = signal(false);
  readonly hideReadonly = signal(false);
  readonly hideZeroValued = signal(false);
  readonly findColumnQuery = signal('');
  readonly twoPaneMode = signal(false);
  readonly selectedRowQname = signal<string | null>(null);
  readonly utilityPaneOpen = signal(false);
  readonly pasteText = signal('');
  readonly pasteStatus = signal<string>('');
  readonly fillTargetColumn = signal<string>('');
  readonly fillValue = signal<number | null>(0);

  /** Bumped by fill/zero/undo/paste + edit-mode toggles. */
  private readonly mutationTick = signal(0);

  /** Bumped (debounced) when the FormGroup's value changes — i.e. user typed. */
  private readonly formValueTick = signal(0);

  // Undo stacks per-row. Populated by snapshotRow before a destructive write.
  private undoStacks = new Map<string, RowSnapshot[]>();
  private readonly undoCap = 10;

  private scrollParent: HTMLElement | null = null;

  constructor() {
    afterNextRender(() => {
      this.attachScrollListener();
      this.attachHotkeyListener();
      this.attachFormTick();
      this.buildColumnSumBadgeMap();
    });
  }

  /** Debounced tick bumped on form value changes — drives memoized recomputes. */
  private attachFormTick(): void {
    if (!this.form) return;
    this.form.valueChanges
      .pipe(debounceTime(DEBUG_FORM_TICK_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formValueTick.update((version) => version + 1));
  }

  // ─── Scroll-to-top plumbing ───────────────────────────────────────
  private attachScrollListener(): void {
    const target = this.findScrollParent(this.hostEl.nativeElement);
    this.scrollParent = target;
    const threshold = () =>
      target === document.documentElement ? window.innerHeight : target.clientHeight;

    this.zone.runOutsideAngular(() => {
      const handler = () => {
        const visible = target.scrollTop > threshold();
        if (visible !== this.showScrollTop()) {
          this.zone.run(() => this.showScrollTop.set(visible));
        }
      };
      target.addEventListener('scroll', handler, { passive: true });
      this.destroyRef.onDestroy(() => target.removeEventListener('scroll', handler));
    });
  }

  private findScrollParent(el: HTMLElement): HTMLElement {
    let node: HTMLElement | null = el.parentElement;
    while (node && node !== document.body) {
      const style = window.getComputedStyle(node);
      const overflowY = style.overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        return node;
      }
      node = node.parentElement;
    }
    return document.documentElement;
  }

  scrollToTop(): void {
    const target: HTMLElement = this.scrollParent ?? document.documentElement;
    target.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ─── Hotkeys ──────────────────────────────────────────────────────
  private attachHotkeyListener(): void {
    const handler = (event: KeyboardEvent) => {
      // Ctrl+Alt+E / Ctrl+Alt+Q — next / previous failing row.
      // Ctrl+Alt is added to dodge Chrome's Alt+E customize-menu shortcut.
      if (event.ctrlKey && event.altKey && !event.metaKey && !event.shiftKey) {
        if (event.code === 'KeyE') {
          event.preventDefault();
          this.jumpToError(1);
          return;
        }
        if (event.code === 'KeyQ') {
          event.preventDefault();
          this.jumpToError(-1);
          return;
        }
      }
      // Alt+↑ / Alt+↓ — move two-pane selection.
      if (event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey && this.twoPaneMode()) {
        if (event.code === 'ArrowDown') {
          event.preventDefault();
          this.moveSelection(1);
        } else if (event.code === 'ArrowUp') {
          event.preventDefault();
          this.moveSelection(-1);
        }
      }
    };
    window.addEventListener('keydown', handler);
    this.destroyRef.onDestroy(() => window.removeEventListener('keydown', handler));
  }

  /** The most recently jumped-to failed card. Held as a DOM reference so
   *  we can locate its index in the live failed-cards list on the next
   *  keypress and step forward / backward by exactly one. */
  private lastJumpTarget: HTMLElement | null = null;

  jumpToError(direction: 1 | -1): void {
    const root = this.hostEl.nativeElement as HTMLElement;
    const cards = Array.from(
      root.querySelectorAll('.debug-row-card.row-failed'),
    ) as HTMLElement[];
    if (cards.length === 0) return;

    // Sort by on-screen vertical position so "next" and "previous" follow
    // visual order even if querySelectorAll returns nodes out of order.
    const items = cards
      .map((el) => ({ el, top: el.getBoundingClientRect().top }))
      .sort((firstItem, secondItem) => firstItem.top - secondItem.top);

    // Locate our anchor. If the last target is still in the list, walk
    // from its index by ±1 (wrapping). Otherwise seed fresh from where
    // the viewport currently sits.
    const anchored = this.lastJumpTarget
      ? items.findIndex((item) => item.el === this.lastJumpTarget)
      : -1;

    let idx: number;
    if (anchored >= 0) {
      idx = (anchored + direction + items.length) % items.length;
    } else {
      const midY = window.innerHeight / 2;
      if (direction === 1) {
        const seed = items.findIndex((item) => item.top > midY);
        idx = seed === -1 ? 0 : seed;
      } else {
        // Last card whose top is above the midline.
        let seed = -1;
        for (let itemIndex = items.length - 1; itemIndex >= 0; itemIndex--) {
          if (items[itemIndex].top < midY) { seed = itemIndex; break; }
        }
        idx = seed === -1 ? items.length - 1 : seed;
      }
    }

    const target = items[idx].el;
    this.lastJumpTarget = target;
    // Card may be collapsed — open it before scrolling so the error is visible.
    if (target instanceof HTMLDetailsElement && !target.open) target.open = true;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('debug-err-flash');
    window.setTimeout(() => target.classList.remove('debug-err-flash'), 1200);
  }

  moveSelection(direction: 1 | -1): void {
    const rows = this.visibleDataRows();
    if (rows.length === 0) return;
    const current = this.selectedRowQname();
    const idx = rows.findIndex((row) => row.qname === current);
    const next = idx < 0
      ? rows[0]
      : rows[(idx + direction + rows.length) % rows.length];
    this.selectedRowQname.set(next.qname);
    queueMicrotask(() => {
      const root = this.hostEl.nativeElement as HTMLElement;
      const el = root.querySelector(
        `[data-row-qname="${CSS.escape(next.qname)}"]`,
      ) as HTMLElement | null;
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  // ─── Consolidation + rows ─────────────────────────────────────────
  consolidationTypes(): string[] {
    const types: string[] = [];
    if (this.host.showConsolidatedColumns) types.push('Consolidated');
    types.push('Separate');
    return types;
  }

  dataRows(): DebugFieldControl[] {
    const controls = this.schema?.controls ?? [];
    return controls.filter(
      (control) => control.ui?.controlType === 'number' && control.ui?.equityColumns && !this.host.isSectionHeader(control),
    );
  }

  /** Columns narrowed by the find query. Computed so it runs once per change. */
  readonly filteredColumns = computed<DebugEquityColumn[]>(() => {
    const query = this.findColumnQuery().trim().toLowerCase();
    const cols = this.schema?.equityColumns ?? [];
    if (!query) return cols;
    return cols.filter(
      (control) => control.label.toLowerCase().includes(query) || control.qname.toLowerCase().includes(query),
    );
  });

  /** Rows after applying the banner filter toggles (but not per-consolidation
   *  failure filtering — that needs consolidationType context).
   *
   *  Memoized via computed() so the expensive hideZeroValued scan only runs
   *  when the relevant filter signals (or form values, when hideZero is on)
   *  actually change. */
  private readonly visibleDataRowsComputed = computed<DebugFieldControl[]>(() => {
    // If the find query narrowed columns to zero, the row cards would be
    // empty anyway — hide them all.
    if (this.filteredColumns().length === 0) return [];

    const hideCalc = this.hideCalculated();
    const hideRo = this.hideReadonly();
    const hideZero = this.hideZeroValued();
    this.mutationTick();
    // Only depend on form values when hideZero is active (typing inputs would
    // otherwise re-run the full-row scan on every keystroke).
    if (hideZero) this.formValueTick();

    let rows = this.dataRows();
    if (hideCalc) rows = rows.filter((row) => !this.host.isCalculatedField(row));
    if (hideRo) rows = rows.filter((row) => !row.ui.readonly);
    if (hideZero) rows = rows.filter((row) => !this.isRowAllZero(row));
    return rows;
  });

  visibleDataRows(): DebugFieldControl[] {
    return this.visibleDataRowsComputed();
  }

  shouldRenderRow(row: DebugFieldControl, consolidationType: string): boolean {
    if (!this.host.shouldShowRow(row, this.rowIndex(row), consolidationType, 'Current')) return false;
    if (this.onlyFailing() && !this.rowFailed(row, consolidationType)) return false;
    return this.visibleDataRowsComputed().some((candidate) => candidate.qname === row.qname);
  }

  rowIndex(control: DebugFieldControl): number {
    return this.schema?.controls?.indexOf(control) ?? -1;
  }

  private isRowAllZero(row: DebugFieldControl): boolean {
    for (const ct of this.consolidationTypes()) {
      for (const period of ['Current', 'Previous'] as Period[]) {
        if (period === 'Previous' && !this.host.hasPreviousYear) continue;
        for (const col of this.schema?.equityColumns ?? []) {
          const key = this.host.getEquityColumnControlName(row.qname, col.qname, ct, period);
          const controlValue = this.form.get(key)?.value;
          if (controlValue !== null && controlValue !== undefined && Number(controlValue) !== 0) return false;
        }
      }
    }
    return true;
  }

  delta(row: DebugFieldControl, column: DebugEquityColumn, consolidationType: string): number | null {
    if (!this.host.hasPreviousYear) return null;
    const curKey = this.host.getEquityColumnControlName(row.qname, column.qname, consolidationType, 'Current');
    const prevKey = this.host.getEquityColumnControlName(row.qname, column.qname, consolidationType, 'Previous');
    const cur = this.form.get(curKey)?.value;
    const prev = this.form.get(prevKey)?.value;
    if (cur == null || prev == null) return null;
    const delta = Number(cur) - Number(prev);
    return Number.isNaN(delta) ? null : delta;
  }

  /**
   * Short chip labels rendered in the "Errors" column for a given cell.
   *
   * Sources merged (de-duplicated as a Set):
   *   1. Cell-level predicates on the host (`hasRequiredError`, `hasColumnSumError`, …)
   *      — these already surface the well-known mismatches that map 1:1 to a single cell.
   *   2. Row-level messages from `host.getRowValidationErrors(row)` filtered to the
   *      current `[consolidationType periodType]` table. Each row-level message is
   *      classified against the schema's `columnSumValidations` entries (plus the
   *      hardcoded equity-total marker) and routed to the matching subtotal column.
   *      Messages that don't classify are dropped from the chip list on purpose —
   *      the `<details>` ERROR block under the row already renders the full text.
   *
   * If a single cell ends up with more than one distinct chip label after
   * de-duplication, the list is collapsed into a single generic
   * "Multiple errors" chip so the button text stays scannable.
   */
  cellErrors(
    row: DebugFieldControl,
    column: DebugEquityColumn,
    consolidationType: string,
    periodType: Period,
  ): string[] {
    const errs = new Set<string>();

    if (this.host.hasRequiredError(row, column.qname, consolidationType, periodType)) errs.add('Required');
    if (this.host.hasCrossSheetError(row.qname, column.qname, consolidationType, periodType)) errs.add('Cross-sheet mismatch');
    if (this.host.hasColumnSumError(row, column.qname, consolidationType, periodType)) errs.add('Column sum mismatch');
    if (this.host.hasEquityTotalError(row, column.qname, consolidationType, periodType)) errs.add('Equity total mismatch');
    if (this.host.hasEquityBeginningEndError(row, column.qname, consolidationType, periodType)) errs.add('Begin/End period mismatch');

    const rowMessages = this.host.getRowValidationErrors(row.qname);
    if (rowMessages.length > 0) {
      const tableIdentifier = `[${consolidationType} ${periodType}]`;
      const sumConfigs = this.host.getColumnSumValidations();
      for (const msg of rowMessages) {
        if (!msg.includes(tableIdentifier)) continue;
        const chip = this.classifyRowErrorForColumn(msg, column.qname, sumConfigs);
        if (chip) errs.add(chip);
      }
    }

    if (errs.size > 1) return ['Multiple errors'];
    return Array.from(errs);
  }

  /**
   * Classify a row-level validation message and decide whether it belongs
   * on the given column's cell. Returns the short chip label or `null` to
   * drop (the full text still shows in the `<details>` block below).
   */
  private classifyRowErrorForColumn(
    message: string,
    columnQname: string,
    sumConfigs: DebugColumnSumConfig[],
  ): string | null {
    // Equity-total horizontal check lives on the `EquityMember` column only.
    if (message.includes('Total should equal to the sum of')) {
      return columnQname === 'ifrs-full:EquityMember' ? 'Equity total mismatch' : null;
    }
    // Column-sum checks are schema-driven: the message is prefixed with the
    // `subTotalLabel` of exactly one config entry; its `subTotalColumnQname`
    // is the cell we want to anchor the chip on.
    if (message.includes('should equal to the sum of')) {
      const match = sumConfigs.find(cfg => message.startsWith(`${cfg.subTotalLabel} [`));
      if (match && match.subTotalColumnQname === columnQname) return 'Column sum mismatch';
      return null;
    }
    return null;
  }

  rowFailed(control: DebugFieldControl, consolidationType: string): boolean {
    const curFailed = this.host.getRowValidationStatus(control, consolidationType, 'Current') === 'Failed';
    const prevFailed =
      this.host.hasPreviousYear &&
      this.host.getRowValidationStatus(control, consolidationType, 'Previous') === 'Failed';
    return curFailed || prevFailed;
  }

  // ─── Summary bar counts ───────────────────────────────────────────
  /** Computes all consolidations' summaries in one pass, so the template's
   *  repeated `summaryFor(...)` calls turn into O(1) map lookups. */
  private readonly summariesComputed = computed(() => {
    // Re-read the tracked inputs so invalidation is automatic.
    this.mutationTick();
    this.formValueTick();
    void this.onlyFailing();
    void this.hideCalculated();
    void this.hideReadonly();
    void this.hideZeroValued();
    void this.findColumnQuery();

    const rows = this.visibleDataRowsComputed();
    const periods: Period[] = this.host.hasPreviousYear ? ['Current', 'Previous'] : ['Current'];
    const columns = this.schema?.equityColumns ?? [];

    const result = new Map<string, ReturnType<typeof this.emptySummary>>();
    for (const ct of this.consolidationTypes()) {
      let failing = 0, required = 0, crossSheet = 0, columnSum = 0, equityTotal = 0, beginEnd = 0;
      for (const row of rows) {
        if (!this.host.shouldShowRow(row, this.rowIndex(row), ct, 'Current')) continue;
        if (this.rowFailed(row, ct)) failing++;
        for (const period of periods) {
          for (const col of columns) {
            if (this.host.hasRequiredError(row, col.qname, ct, period)) required++;
            if (this.host.hasCrossSheetError(row.qname, col.qname, ct, period)) crossSheet++;
            if (this.host.hasColumnSumError(row, col.qname, ct, period)) columnSum++;
            if (this.host.hasEquityTotalError(row, col.qname, ct, period)) equityTotal++;
            if (this.host.hasEquityBeginningEndError(row, col.qname, ct, period)) beginEnd++;
          }
        }
      }
      result.set(ct, { failing, required, crossSheet, columnSum, equityTotal, beginEnd });
    }
    return result;
  });

  private emptySummary() {
    return { failing: 0, required: 0, crossSheet: 0, columnSum: 0, equityTotal: 0, beginEnd: 0 };
  }

  summaryFor(consolidationType: string) {
    return this.summariesComputed().get(consolidationType) ?? this.emptySummary();
  }

  // ─── Equality group badges ────────────────────────────────────────
  /** Built once from the schema on first render so per-cell lookup is O(1). */
  private columnSumBadgeMap: Map<string, { role: 'subtotal' | 'component'; tooltip: string }> | null = null;

  private buildColumnSumBadgeMap(): void {
    const map = new Map<string, { role: 'subtotal' | 'component'; tooltip: string }>();
    for (const validation of this.host.getColumnSumValidations()) {
      map.set(validation.subTotalColumnQname, {
        role: 'subtotal',
        tooltip: `Σ Subtotal of: ${validation.componentLabels.join(', ')}`,
      });
      for (const qname of validation.componentColumnQnames) {
        if (!map.has(qname)) {
          map.set(qname, { role: 'component', tooltip: `Component of: ${validation.subTotalLabel}` });
        }
      }
    }
    this.columnSumBadgeMap = map;
  }

  columnSumBadge(column: DebugEquityColumn): { role: 'subtotal' | 'component'; tooltip: string } | null {
    if (!this.columnSumBadgeMap) this.buildColumnSumBadgeMap();
    return this.columnSumBadgeMap!.get(column.qname) ?? null;
  }

  // ─── Row actions: copy / fill / zero / undo ───────────────────────
  private collectRowKeys(row: DebugFieldControl): string[] {
    const keys: string[] = [];
    for (const ct of this.consolidationTypes()) {
      for (const period of ['Current', 'Previous'] as Period[]) {
        if (period === 'Previous' && !this.host.hasPreviousYear) continue;
        for (const col of this.schema?.equityColumns ?? []) {
          keys.push(this.host.getEquityColumnControlName(row.qname, col.qname, ct, period));
        }
      }
    }
    return keys;
  }

  private snapshotRow(row: DebugFieldControl): void {
    const snap: RowSnapshot = {};
    for (const key of this.collectRowKeys(row)) {
      snap[key] = this.form.get(key)?.value ?? null;
    }
    const stack = this.undoStacks.get(row.qname) ?? [];
    stack.push(snap);
    if (stack.length > this.undoCap) stack.shift();
    this.undoStacks.set(row.qname, stack);
    this.mutationTick.update((version) => version + 1);
  }

  hasUndo(row: DebugFieldControl): boolean {
    this.mutationTick();
    return (this.undoStacks.get(row.qname) ?? []).length > 0;
  }

  undoRow(row: DebugFieldControl): void {
    const stack = this.undoStacks.get(row.qname) ?? [];
    const snap = stack.pop();
    if (!snap) return;
    let wrote = 0;
    for (const [key, val] of Object.entries(snap)) {
      if (this.host.setFieldValue(key, val)) wrote++;
    }
    if (wrote === 0) this.flashMessage('Undo blocked: row is not in edit mode.');
    else this.flashMessage(`Undo applied (${wrote} fields).`);
    this.mutationTick.update((version) => version + 1);
  }

  copyRowJson(row: DebugFieldControl): void {
    const data: RowSnapshot = {};
    for (const key of this.collectRowKeys(row)) {
      data[key] = this.form.get(key)?.value ?? null;
    }
    const text = JSON.stringify(data, null, 2);
    navigator.clipboard?.writeText(text).then(
      () => this.flashMessage('Row JSON copied to clipboard.'),
      () => this.flashMessage('Clipboard write failed — fallback: see browser console.', true),
    ).catch(() => {
      console.info('[role19 debug] copyRowJson payload:', text);
    });
  }

  fillRow(row: DebugFieldControl, rawValue: number | null): void {
    this.snapshotRow(row);
    let wrote = 0, skipped = 0;
    for (const key of this.collectRowKeys(row)) {
      if (this.host.setFieldValue(key, rawValue)) wrote++; else skipped++;
    }
    if (wrote === 0) {
      this.flashMessage('Fill blocked: enter edit mode on this row first.', true);
    } else {
      this.flashMessage(`Filled ${wrote} fields${skipped ? ` (${skipped} skipped: disabled/calculated)` : ''}.`);
    }
  }

  zeroRow(row: DebugFieldControl): void {
    this.fillRow(row, 0);
  }

  promptFillRow(row: DebugFieldControl): void {
    const input = window.prompt(`Fill every non-calculated cell in "${row.label.replace(/<[^>]+>/g, '')}" with:`, '0');
    if (input === null) return;
    const parsed = parseFloat(input);
    if (Number.isNaN(parsed)) { this.flashMessage('Fill cancelled: not a number.', true); return; }
    this.fillRow(row, parsed);
  }

  // ─── Column-fill + paste utility ─────────────────────────────────
  fillColumn(): void {
    const columnQname = this.fillTargetColumn();
    const value = this.fillValue();
    if (!columnQname || value === null) return;
    const rows = this.visibleDataRows();
    let wrote = 0, skipped = 0;
    for (const row of rows) {
      for (const ct of this.consolidationTypes()) {
        for (const period of ['Current', 'Previous'] as Period[]) {
          if (period === 'Previous' && !this.host.hasPreviousYear) continue;
          const key = this.host.getEquityColumnControlName(row.qname, columnQname, ct, period);
          if (this.host.setFieldValue(key, value)) wrote++; else skipped++;
        }
      }
    }
    this.flashMessage(`Filled ${wrote} cells${skipped ? ` (${skipped} skipped)` : ''}.`, wrote === 0);
  }

  applyPaste(): void {
    const text = this.pasteText().trim();
    if (!text) { this.pasteStatus.set('Nothing to paste.'); return; }
    let parsed: Record<string, unknown>;
    try { parsed = JSON.parse(text); }
    catch { this.pasteStatus.set('Paste failed: invalid JSON.'); return; }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      this.pasteStatus.set('Paste failed: expected an object of { controlName: value }.');
      return;
    }
    let wrote = 0, skipped = 0, missing = 0;
    for (const [key, raw] of Object.entries(parsed)) {
      const ctrl = this.form.get(key);
      if (!ctrl) { missing++; continue; }
      const parsed = raw === null ? null : Number(raw);
      if (parsed !== null && Number.isNaN(parsed)) { skipped++; continue; }
      if (this.host.setFieldValue(key, parsed)) wrote++; else skipped++;
    }
    this.pasteStatus.set(
      `Wrote ${wrote}${skipped ? ` · skipped ${skipped}` : ''}${missing ? ` · unknown keys ${missing}` : ''}`,
    );
  }

  exportAllJson(): void {
    const raw = this.form?.getRawValue?.() ?? {};
    const text = JSON.stringify(raw, null, 2);
    navigator.clipboard?.writeText(text).then(
      () => this.flashMessage('Full form JSON copied to clipboard.'),
      () => {
        console.info('[role19 debug] exportAllJson payload:', text);
        this.flashMessage('Clipboard write failed — full JSON logged to console.', true);
      },
    );
  }

  // ─── Form stats ──────────────────────────────────────────────────
  readonly formStats = computed(() => {
    // Re-evaluate on form writes + explicit mutations (which include
    // enable/disable toggled by edit mode).
    this.formValueTick();
    this.mutationTick();
    return this.host.getFormStats();
  });

  // ─── Two-pane selection ───────────────────────────────────────────
  readonly paneRows = computed(() => this.visibleDataRowsComputed());

  selectRow(row: DebugFieldControl): void {
    this.selectedRowQname.set(row.qname);
  }

  selectedRow(): DebugFieldControl | null {
    const selectedQname = this.selectedRowQname();
    if (!selectedQname) return null;
    return this.visibleDataRowsComputed().find((row) => row.qname === selectedQname) ?? null;
  }

  // ─── Editability helpers ──────────────────────────────────────────
  isEditable(control: DebugFieldControl): boolean {
    return !this.host.isCalculatedField(control) && !control.ui.readonly;
  }

  editableRows(): DebugFieldControl[] {
    return this.dataRows().filter((row) => this.isEditable(row));
  }

  // The debug layout's row card groups both periods of a single consolidation under one
  // Edit button (Current and Previous shown side-by-side). The host's edit-mode methods
  // are scoped per (consolidation, period) since the 2026-05 fix to the bleeding-edit bug,
  // so each user gesture here fans out to one call per visible period.
  private periodsForConsolidation(): string[] {
    return this.host.hasPreviousYear ? ['Current', 'Previous'] : ['Current'];
  }

  isRowInEditModeForConsolidation(row: DebugFieldControl, consolidationType: string): boolean {
    return this.periodsForConsolidation().some(period => this.host.isRowInEditMode(row, consolidationType, period));
  }

  private isRowInEditModeAnyTable(row: DebugFieldControl): boolean {
    return this.consolidationTypes().some(consolidation => this.isRowInEditModeForConsolidation(row, consolidation));
  }

  allEditableInEditMode(): boolean {
    const rows = this.editableRows();
    if (rows.length === 0) return false;
    const consolidations = this.consolidationTypes();
    const periods = this.periodsForConsolidation();
    return rows.every((row) =>
      consolidations.every(cons =>
        periods.every(period => this.host.isRowInEditMode(row, cons, period))));
  }

  anyEditableInEditMode(): boolean {
    return this.editableRows().some((row) => this.isRowInEditModeAnyTable(row));
  }

  toggleEditAll(): void {
    const rows = this.editableRows();
    const consolidations = this.consolidationTypes();
    const periods = this.periodsForConsolidation();
    if (this.anyEditableInEditMode()) {
      rows.forEach((row) => {
        consolidations.forEach(cons => {
          periods.forEach(period => {
            if (this.host.isRowInEditMode(row, cons, period)) this.host.saveEditMode(row, cons, period);
          });
        });
      });
    } else {
      rows.forEach((row) => {
        consolidations.forEach(cons => {
          periods.forEach(period => {
            if (!this.host.isRowInEditMode(row, cons, period)) this.host.enterEditMode(row, cons, period);
          });
        });
      });
    }
    this.mutationTick.update((version) => version + 1);
  }

  // Thin wrappers around host edit-mode methods so per-row Edit/Save/Cancel
  // clicks also nudge the mutation tick (keeps stats pill + summary counts
  // in sync with the enabled/disabled state of controls). The row card shows
  // both Current and Previous side-by-side, so a single click fans out across
  // the visible periods of the given consolidation.
  enterEdit(row: DebugFieldControl, consolidationType: string): void {
    this.periodsForConsolidation().forEach(period => this.host.enterEditMode(row, consolidationType, period));
    this.mutationTick.update((version) => version + 1);
  }
  saveEdit(row: DebugFieldControl, consolidationType: string): void {
    this.periodsForConsolidation().forEach(period => this.host.saveEditMode(row, consolidationType, period));
    this.mutationTick.update((version) => version + 1);
  }
  cancelEdit(row: DebugFieldControl, consolidationType: string): void {
    this.periodsForConsolidation().forEach(period => this.host.cancelEditMode(row, consolidationType, period));
    this.mutationTick.update((version) => version + 1);
  }

  // ─── Toast-lite ───────────────────────────────────────────────────
  readonly toastMessage = signal<{ text: string; error: boolean } | null>(null);
  private toastTimeout: ReturnType<typeof setTimeout> | null = null;

  private flashMessage(text: string, error = false): void {
    this.toastMessage.set({ text, error });
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    this.toastTimeout = setTimeout(() => this.toastMessage.set(null), 2200);
  }

  // ─── Error-description reveal (click on cell chip) ────────────────
  errorDetailsId(
    consolidationType: string,
    row: DebugFieldControl,
    period: Period,
  ): string {
    return `debug-err-${consolidationType}-${row.qname}-${period}`;
  }

  revealErrorDetails(
    consolidationType: string,
    row: DebugFieldControl,
    period: Period,
  ): void {
    const details = document.getElementById(
      this.errorDetailsId(consolidationType, row, period),
    ) as HTMLDetailsElement | null;
    if (!details) return;
    // Also open the ancestor row card if it was collapsed — otherwise the
    // error-details node is hidden and scrollIntoView lands on nothing.
    const card = details.closest('details.debug-row-card') as HTMLDetailsElement | null;
    if (card && !card.open) card.open = true;
    details.open = true;
    details.scrollIntoView({ behavior: 'smooth', block: 'center' });
    details.classList.add('debug-err-flash');
    window.setTimeout(() => details.classList.remove('debug-err-flash'), 1200);
  }

  trackByQname(_unusedIndex: number, item: { qname: string }): string { return item.qname; }
  trackByValue(_unusedIndex: number, item: string): string { return item; }
}
